import { useState, useEffect, useCallback, type ElementType, type ReactNode } from 'react'
import {
  Plus, Trash2, Calendar, ChevronDown, ChevronUp,
  FileText, Users, BarChart3, ClipboardList, Download, X, Save, Loader2,
} from 'lucide-react'
import { sowTemplateApi, type SowTemplateType } from '@api/client'
import { showToast } from '@store/uiStore'
import type {
  SowConfig,
  RaciValue,
  GanttParty,
  RaciColumn,
  RaciEntry,
  SowMilestone,
  SowPhase,
  GanttTask,
} from '@features/scope/types'
import {
  getDefaultSowConfig,
  getDefaultRaciTemplate,
  getDefaultGanttTemplate,
} from '@features/scope/utils/defaults'

// ── Constants ──────────────────────────────────────────────────────────────

const SOW_TYPE_OPTIONS: { value: SowTemplateType; label: string; description: string }[] = [
  { value: 'global', label: 'Standard', description: 'Default SOW for new customers' },
  { value: 'upsell', label: 'Upsell', description: 'Existing customer adding services' },
  { value: 'reseller_parent', label: 'Reseller Parent', description: 'Reseller partner agreement' },
  { value: 'reseller_child', label: 'Reseller Child', description: 'End customer via reseller' },
]

const RACI_OPTIONS: { value: RaciValue; label: string }[] = [
  { value: '', label: '-' },
  { value: 'R', label: 'Responsible' },
  { value: 'A', label: 'Accountable' },
  { value: 'C', label: 'Consulted' },
  { value: 'I', label: 'Informed' },
]

const RACI_COLORS: Record<string, string> = {
  R: 'bg-blue-100 text-blue-800',
  A: 'bg-green-100 text-green-800',
  C: 'bg-yellow-100 text-yellow-800',
  I: 'bg-gray-100 text-gray-600',
  '': '',
}

// ── Section component (defined outside to avoid remount-on-rerender) ──────

interface SectionProps {
  id: string
  icon: ElementType
  iconColor: string
  title: string
  children: ReactNode
  expandedSections: Record<string, boolean>
  onToggle: (id: string) => void
}

function Section({ id, icon: Icon, iconColor, title, children, expandedSections, onToggle }: SectionProps) {
  return (
    <div className="bg-[var(--bg-tertiary)]/40 border border-[var(--border-color)] rounded-lg">
      <button
        type="button"
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-[var(--bg-secondary)] transition-colors"
        onClick={() => onToggle(id)}
      >
        <div className="flex items-center gap-2">
          <Icon size={18} className={iconColor} />
          <h4 className="text-lg font-semibold text-[var(--text-primary)]">{title}</h4>
        </div>
        {expandedSections[id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {expandedSections[id] && (
        <div className="p-4 border-t border-[var(--border-color)] space-y-4">{children}</div>
      )}
    </div>
  )
}

// ── Component ──────────────────────────────────────────────────────────────

export function SowTemplateEditorPage() {
  const [sowType, setSowType] = useState<SowTemplateType>('global')
  const [config, setConfig] = useState<SowConfig>(getDefaultSowConfig())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [updatedBy, setUpdatedBy] = useState<string | null>(null)
  const [updatedAt, setUpdatedAt] = useState<string | null>(null)

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    legal: false,
    deliveryNotes: true,
    phasesVariation: true,
    commercials: true,
    raci: false,
    gantt: false,
    keyAssumptions: false,
  })

  // ── Load template on mount ──

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        const res = await sowTemplateApi.get(sowType)
        if (cancelled) return
        if (res.success && res.data) {
          const merged = { ...getDefaultSowConfig(sowType), ...(res.data.config as Partial<SowConfig>) }
          setConfig(merged)
          setUpdatedBy(res.data.updatedByName ?? res.data.updatedBy ?? null)
          setUpdatedAt(res.data.updatedAt ?? null)
        }
      } catch {
        showToast('Failed to load SOW template', 'error')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [sowType])

  // ── Save handler ──

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      const res = await sowTemplateApi.save(config as unknown as Record<string, unknown>, sowType)
      if (res.success && res.data) {
        setUpdatedBy(res.data.updatedByName ?? res.data.updatedBy ?? null)
        setUpdatedAt(res.data.updatedAt ?? null)
        const typeLabel = SOW_TYPE_OPTIONS.find((o) => o.value === sowType)?.label || sowType
        showToast(`${typeLabel} SOW template saved`, 'success')
      } else {
        showToast(res.error || 'Failed to save template', 'error')
      }
    } catch {
      showToast('Failed to save SOW template', 'error')
    } finally {
      setSaving(false)
    }
  }, [config, sowType])

  // ── Config updater helpers ──

  const updateField = <K extends keyof SowConfig>(key: K, value: SowConfig[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }))
  }

  // ── Milestone helpers ──

  const addMilestone = () => {
    setConfig((prev) => ({
      ...prev,
      milestones: [...prev.milestones, { id: Date.now(), name: '', date: '', description: '' }],
    }))
  }

  const updateMilestone = (id: number, patch: Partial<SowMilestone>) => {
    setConfig((prev) => ({
      ...prev,
      milestones: prev.milestones.map((m) => (m.id === id ? { ...m, ...patch } : m)),
    }))
  }

  const removeMilestone = (id: number) => {
    setConfig((prev) => ({ ...prev, milestones: prev.milestones.filter((m) => m.id !== id) }))
  }

  // ── Phase helpers ──

  const addPhase = () => {
    setConfig((prev) => ({
      ...prev,
      phases: [...prev.phases, { id: Date.now(), name: '', description: '', duration: '', startDate: '', endDate: '', deliverables: [] }],
    }))
  }

  const updatePhase = (id: number, patch: Partial<SowPhase>) => {
    setConfig((prev) => ({
      ...prev,
      phases: prev.phases.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    }))
  }

  const removePhase = (id: number) => {
    setConfig((prev) => ({ ...prev, phases: prev.phases.filter((p) => p.id !== id) }))
  }

  // ── RACI helpers ──

  const addRaciColumn = (name: string) => {
    const col: RaciColumn = { id: `col_${Date.now()}`, name }
    setConfig((prev) => ({ ...prev, raciColumns: [...prev.raciColumns, col] }))
  }

  const removeRaciColumn = (colId: string) => {
    setConfig((prev) => ({
      ...prev,
      raciColumns: prev.raciColumns.filter((c) => c.id !== colId),
      raciEntries: prev.raciEntries.map((e) => {
        const { [colId]: _, ...rest } = e.assignments
        return { ...e, assignments: rest }
      }),
    }))
  }

  const updateRaciColumnName = (colId: string, name: string) => {
    setConfig((prev) => ({
      ...prev,
      raciColumns: prev.raciColumns.map((c) => (c.id === colId ? { ...c, name } : c)),
    }))
  }

  const addRaciEntry = () => {
    const assignments: Record<string, RaciValue> = {}
    config.raciColumns.forEach((c) => { assignments[c.id] = '' })
    const entry: RaciEntry = { id: Date.now(), task: '', assignments }
    setConfig((prev) => ({ ...prev, raciEntries: [...prev.raciEntries, entry] }))
  }

  const updateRaciEntryTask = (id: number, task: string) => {
    setConfig((prev) => ({
      ...prev,
      raciEntries: prev.raciEntries.map((e) => (e.id === id ? { ...e, task } : e)),
    }))
  }

  const updateRaciAssignment = (entryId: number, colId: string, value: RaciValue) => {
    setConfig((prev) => ({
      ...prev,
      raciEntries: prev.raciEntries.map((e) =>
        e.id === entryId ? { ...e, assignments: { ...e.assignments, [colId]: value } } : e
      ),
    }))
  }

  const removeRaciEntry = (id: number) => {
    setConfig((prev) => ({ ...prev, raciEntries: prev.raciEntries.filter((e) => e.id !== id) }))
  }

  const loadDefaultRaci = () => {
    const { columns, entries } = getDefaultRaciTemplate()
    setConfig((prev) => ({ ...prev, raciColumns: columns, raciEntries: entries }))
  }

  // ── Gantt helpers ──

  const addGanttTask = () => {
    const task: GanttTask = { id: Date.now(), phaseId: 0, taskName: '', startDate: '', endDate: '', dependencies: [], resources: [], responsiblePerson: '', party: '' }
    setConfig((prev) => ({ ...prev, ganttTasks: [...prev.ganttTasks, task] }))
  }

  const updateGanttTask = (id: number, patch: Partial<GanttTask>) => {
    setConfig((prev) => ({
      ...prev,
      ganttTasks: prev.ganttTasks.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    }))
  }

  const removeGanttTask = (id: number) => {
    setConfig((prev) => ({ ...prev, ganttTasks: prev.ganttTasks.filter((t) => t.id !== id) }))
  }

  const loadDefaultGantt = () => {
    setConfig((prev) => ({ ...prev, ganttTasks: getDefaultGanttTemplate() }))
  }

  // ── Section toggle ──

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  // ── Loading state ──

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin text-primary" size={32} />
        <span className="ml-3 text-[var(--text-muted)]">Loading SOW template...</span>
      </div>
    )
  }

  // ── Render ──

  const activeTypeOption = SOW_TYPE_OPTIONS.find((o) => o.value === sowType)

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 pb-24">
      {/* SOW Type Selector */}
      <div className="flex gap-2 mb-6">
        {SOW_TYPE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setSowType(opt.value)}
            className={`px-4 py-2.5 rounded-lg border text-sm transition-colors ${
              sowType === opt.value
                ? 'border-primary bg-primary/10 text-[var(--text-primary)] font-medium'
                : 'border-[var(--border-color)] text-[var(--text-muted)] hover:border-gray-500 hover:text-[var(--text-primary)]'
            }`}
          >
            <div className="font-medium">{opt.label}</div>
            <div className="text-[10px] mt-0.5 opacity-70">{opt.description}</div>
          </button>
        ))}
      </div>

      {/* Page Header */}
      <div className="border-b border-[var(--border-color)] pb-4 mb-6">
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">{activeTypeOption?.label || 'SOW'} Template</h2>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {sowType === 'global'
            ? 'Default template that provides initial values for all scope SOW tabs.'
            : `Template for ${activeTypeOption?.description?.toLowerCase() || 'this SOW type'}. New templates are seeded from the Standard template.`}
        </p>
        {updatedBy && updatedAt && (
          <p className="text-xs text-[var(--text-muted)] mt-2">
            Last updated by {updatedBy} at {new Date(updatedAt).toLocaleString()}
          </p>
        )}
      </div>

      <div className="space-y-6">
        {/* 1. Legal Text */}
        <Section id="legal" icon={FileText} iconColor="text-primary" title="Legal Text" expandedSections={expandedSections} onToggle={toggleSection}>
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            This legal text is stored but not yet included in generated SOW documents.
          </p>
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">SOW Legal Text</label>
            <p className="text-xs text-[var(--text-muted)] mb-2">
              Standard SOW preamble, scope of services, and out-of-scope clauses. Use <code>[Client Name]</code> and <code>[ABN]</code> tokens — they are auto-substituted from each scope's Customer Name and ABN at display time. Replace <code>[Insert Date]</code> with the actual date.
            </p>
            <textarea
              className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary font-mono leading-relaxed"
              rows={24}
              value={config.legalText || ''}
              onChange={(e) => updateField('legalText', e.target.value)}
              placeholder="This Statement of Work (&quot;SOW&quot;) is issued under the terms of..."
            />
          </div>
        </Section>

        {/* 2. Delivery Notes */}
        <Section id="deliveryNotes" icon={Users} iconColor="text-primary" title="Delivery Notes" expandedSections={expandedSections} onToggle={toggleSection}>
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Delivery Notes</label>
            <textarea
              className="w-full min-h-32 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary resize-y"
              placeholder="e.g., Agile delivery with 2-week sprints, weekly status reports, dedicated project channel..."
              value={config.deliveryNotes || ''}
              onChange={(e) => updateField('deliveryNotes', e.target.value)}
            />
          </div>
        </Section>

        {/* 3. Phases & Variation */}
        <Section id="phasesVariation" icon={Calendar} iconColor="text-primary" title="Phases & Variation" expandedSections={expandedSections} onToggle={toggleSection}>
          {/* Milestones */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-[var(--text-primary)]">Key Milestones</label>
              <button
                type="button"
                className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 transition-colors"
                onClick={addMilestone}
              >
                <Plus size={14} />
                Add Milestone
              </button>
            </div>
            {config.milestones.length === 0 ? (
              <div className="text-sm text-[var(--text-muted)] text-center py-6 border border-dashed border-[var(--border-color)] rounded-lg">
                No milestones added yet.
              </div>
            ) : (
              <div className="space-y-3">
                {config.milestones.map((m) => (
                  <div key={m.id} className="border border-[var(--border-color)] rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input type="text" className="h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary" placeholder="Milestone name" value={m.name} onChange={(e) => updateMilestone(m.id, { name: e.target.value })} />
                        <input type="date" className="h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary" value={m.date} onChange={(e) => updateMilestone(m.id, { date: e.target.value })} />
                      </div>
                      <button type="button" className="p-2 text-red-400 hover:bg-red-400/10 rounded transition-colors" onClick={() => removeMilestone(m.id)}><Trash2 size={16} /></button>
                    </div>
                    <textarea className="w-full min-h-20 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary resize-y" placeholder="Milestone description" value={m.description} onChange={(e) => updateMilestone(m.id, { description: e.target.value })} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SOW Phases */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-[var(--text-primary)]">Project Phases</label>
              <button
                type="button"
                className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 transition-colors"
                onClick={addPhase}
              >
                <Plus size={14} />
                Add Phase
              </button>
            </div>
            {config.phases.length === 0 ? (
              <div className="text-sm text-[var(--text-muted)] text-center py-6 border border-dashed border-[var(--border-color)] rounded-lg">
                No phases added yet.
              </div>
            ) : (
              <div className="space-y-3">
                {config.phases.map((phase) => (
                  <div key={phase.id} className="border border-[var(--border-color)] rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input type="text" className="h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary" placeholder="Phase name" value={phase.name} onChange={(e) => updatePhase(phase.id, { name: e.target.value })} />
                        <input type="text" className="h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary" placeholder="Duration (e.g., 4 weeks)" value={phase.duration} onChange={(e) => updatePhase(phase.id, { duration: e.target.value })} />
                      </div>
                      <button type="button" className="p-2 text-red-400 hover:bg-red-400/10 rounded transition-colors" onClick={() => removePhase(phase.id)}><Trash2 size={16} /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-[var(--text-muted)] mb-1">Start Date</label>
                        <input type="date" className="w-full h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary" value={phase.startDate || ''} onChange={(e) => updatePhase(phase.id, { startDate: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-xs text-[var(--text-muted)] mb-1">End Date</label>
                        <input type="date" className="w-full h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary" value={phase.endDate || ''} onChange={(e) => updatePhase(phase.id, { endDate: e.target.value })} />
                      </div>
                    </div>
                    <textarea className="w-full min-h-20 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary resize-y" placeholder="Phase description" value={phase.description} onChange={(e) => updatePhase(phase.id, { description: e.target.value })} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </Section>

        {/* 4. Commercials */}
        <Section id="commercials" icon={BarChart3} iconColor="text-green-500" title="Commercials" expandedSections={expandedSections} onToggle={toggleSection}>
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-3">Fees and Payment Terms</label>
            <p className="text-xs text-[var(--text-muted)] mb-3">Select the pricing model that applies to this SOW.</p>
            <div className="flex gap-3 mb-4">
              {([
                { value: 'fixed', label: 'Fixed Fee' },
                { value: 'tm', label: 'Time & Materials (T&M)' },
              ] as const).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => updateField('commercialsPricingModel', opt.value)}
                  className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                    config.commercialsPricingModel === opt.value
                      ? 'border-green-500 bg-green-500/10 text-[var(--text-primary)] font-medium'
                      : 'border-[var(--border-color)] text-[var(--text-muted)] hover:border-gray-500 hover:text-[var(--text-primary)]'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {config.commercialsPricingModel === 'fixed' && (
              <div className="border border-[var(--border-color)] rounded-lg p-4 space-y-3 bg-[var(--bg-secondary)]/40">
                <p className="text-xs text-[var(--text-muted)] italic">
                  The total fixed fee for the Professional Services described in this SOW is AUD $[amount], payable as per the Agreement.
                </p>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-primary)] mb-1">Total Fixed Fee (AUD)</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[var(--text-muted)]">AUD $</span>
                    <input
                      type="text"
                      className="flex-1 h-9 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary"
                      placeholder="e.g. 25,000"
                      value={config.commercialsFixedFee}
                      onChange={(e) => updateField('commercialsFixedFee', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {config.commercialsPricingModel === 'tm' && (
              <div className="border border-[var(--border-color)] rounded-lg p-4 space-y-3 bg-[var(--bg-secondary)]/40">
                <p className="text-xs text-[var(--text-muted)] italic">
                  Services under this SOW will be billed on a T&M basis at the rates below. Total fees will not exceed the cap without prior written approval.
                </p>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-primary)] mb-1">Hourly Rate (AUD)</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[var(--text-muted)]">AUD $</span>
                    <input
                      type="text"
                      className="flex-1 h-9 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary"
                      placeholder="e.g. 250"
                      value={config.commercialsTmRate}
                      onChange={(e) => updateField('commercialsTmRate', e.target.value)}
                    />
                    <span className="text-sm text-[var(--text-muted)]">/ hour</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-primary)] mb-1">Total Fee Cap (AUD)</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[var(--text-muted)]">AUD $</span>
                    <input
                      type="text"
                      className="flex-1 h-9 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary"
                      placeholder="e.g. 50,000"
                      value={config.commercialsTmCap}
                      onChange={(e) => updateField('commercialsTmCap', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {!config.commercialsPricingModel && (
              <p className="text-xs text-[var(--text-muted)] italic py-2">Select a pricing model above to configure fee terms.</p>
            )}
          </div>
        </Section>

        {/* 5. RACI Chart */}
        <Section id="raci" icon={Users} iconColor="text-purple-500" title="RACI Chart" expandedSections={expandedSections} onToggle={toggleSection}>
          <p className="text-xs text-[var(--text-muted)]">
            R = Responsible, A = Accountable, C = Consulted, I = Informed
          </p>

          {/* Buttons */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
              onClick={() => {
                if (config.raciEntries.length > 0) {
                  if (!confirm('This will replace the current RACI chart with the default template. Continue?')) return
                }
                loadDefaultRaci()
              }}
            >
              <Download size={14} />
              Load Default Template
            </button>
            <button
              type="button"
              className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 transition-colors"
              onClick={() => addRaciColumn('New Role')}
            >
              <Plus size={14} />
              Add Column
            </button>
            <button
              type="button"
              className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 transition-colors"
              onClick={addRaciEntry}
            >
              <Plus size={14} />
              Add Row
            </button>
          </div>

          {/* RACI table */}
          {config.raciColumns.length === 0 && config.raciEntries.length === 0 ? (
            <div className="text-sm text-[var(--text-muted)] text-center py-8 border border-dashed border-[var(--border-color)] rounded-lg">
              No RACI chart yet. Click "Load Default Template" to get started or add columns and rows manually.
            </div>
          ) : (
            <div className="overflow-x-auto border border-[var(--border-color)] rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[var(--bg-secondary)]">
                    <th className="text-left px-3 py-2 border-b border-[var(--border-color)] font-medium text-[var(--text-primary)] min-w-[200px]">Task</th>
                    {config.raciColumns.map((col) => (
                      <th key={col.id} className="px-2 py-2 border-b border-l border-[var(--border-color)] min-w-[120px]">
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            className="w-full bg-transparent border-none text-xs font-medium text-[var(--text-primary)] focus:outline-none text-center"
                            value={col.name}
                            onChange={(e) => updateRaciColumnName(col.id, e.target.value)}
                          />
                          <button type="button" className="text-red-400 hover:text-red-600 flex-shrink-0" onClick={() => removeRaciColumn(col.id)}>
                            <X size={12} />
                          </button>
                        </div>
                      </th>
                    ))}
                    <th className="w-10 border-b border-l border-[var(--border-color)]" />
                  </tr>
                </thead>
                <tbody>
                  {config.raciEntries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-[var(--bg-secondary)]/30">
                      <td className="px-3 py-1.5 border-b border-[var(--border-color)]">
                        <input
                          type="text"
                          className="w-full bg-transparent text-sm text-[var(--text-primary)] focus:outline-none focus:bg-[var(--bg-tertiary)] rounded px-1"
                          placeholder="Task name"
                          value={entry.task}
                          onChange={(e) => updateRaciEntryTask(entry.id, e.target.value)}
                        />
                      </td>
                      {config.raciColumns.map((col) => (
                        <td key={col.id} className={`px-1 py-1.5 border-b border-l border-[var(--border-color)] text-center ${RACI_COLORS[entry.assignments[col.id] || '']}`}>
                          <select
                            className="bg-transparent text-xs font-bold text-center w-full focus:outline-none cursor-pointer"
                            value={entry.assignments[col.id] || ''}
                            onChange={(e) => updateRaciAssignment(entry.id, col.id, e.target.value as RaciValue)}
                          >
                            {RACI_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                      ))}
                      <td className="px-1 py-1.5 border-b border-l border-[var(--border-color)] text-center">
                        <button type="button" className="p-1 text-red-400 hover:bg-red-400/10 rounded" onClick={() => removeRaciEntry(entry.id)}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* 5. Gantt Chart */}
        <Section id="gantt" icon={BarChart3} iconColor="text-indigo-500" title="Gantt Chart" expandedSections={expandedSections} onToggle={toggleSection}>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition-colors"
              onClick={() => {
                if (config.ganttTasks.length > 0) {
                  if (!confirm('This will replace the current Gantt tasks with the default template. Continue?')) return
                }
                loadDefaultGantt()
              }}
            >
              <Download size={14} />
              Load Default Template
            </button>
            <button
              type="button"
              className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 transition-colors"
              onClick={addGanttTask}
            >
              <Plus size={14} />
              Add Task
            </button>
          </div>

          {config.ganttTasks.length === 0 ? (
            <div className="text-sm text-[var(--text-muted)] text-center py-8 border border-dashed border-[var(--border-color)] rounded-lg">
              No Gantt tasks yet. Click "Load Default Template" to get started.
            </div>
          ) : (
            <>
              {/* Gantt table */}
              <div className="overflow-x-auto border border-[var(--border-color)] rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[var(--bg-secondary)]">
                      <th className="text-left px-3 py-2 border-b border-[var(--border-color)] font-medium text-[var(--text-primary)] min-w-[180px]">Task</th>
                      <th className="text-left px-3 py-2 border-b border-l border-[var(--border-color)] font-medium text-[var(--text-primary)] min-w-[140px]">Responsible</th>
                      <th className="text-left px-3 py-2 border-b border-l border-[var(--border-color)] font-medium text-[var(--text-primary)] min-w-[120px]">Party</th>
                      <th className="text-left px-3 py-2 border-b border-l border-[var(--border-color)] font-medium text-[var(--text-primary)] min-w-[140px]">Start Date</th>
                      <th className="text-left px-3 py-2 border-b border-l border-[var(--border-color)] font-medium text-[var(--text-primary)] min-w-[140px]">End Date</th>
                      <th className="w-10 border-b border-l border-[var(--border-color)]" />
                    </tr>
                  </thead>
                  <tbody>
                    {config.ganttTasks.map((task) => (
                      <tr key={task.id} className="hover:bg-[var(--bg-secondary)]/30">
                        <td className="px-2 py-1.5 border-b border-[var(--border-color)]">
                          <input type="text" className="w-full bg-transparent text-sm text-[var(--text-primary)] focus:outline-none focus:bg-[var(--bg-tertiary)] rounded px-1" placeholder="Task name" value={task.taskName} onChange={(e) => updateGanttTask(task.id, { taskName: e.target.value })} />
                        </td>
                        <td className="px-2 py-1.5 border-b border-l border-[var(--border-color)]">
                          <input type="text" className="w-full bg-transparent text-sm text-[var(--text-primary)] focus:outline-none focus:bg-[var(--bg-tertiary)] rounded px-1" placeholder="Person name" value={task.responsiblePerson || ''} onChange={(e) => updateGanttTask(task.id, { responsiblePerson: e.target.value })} />
                        </td>
                        <td className="px-2 py-1.5 border-b border-l border-[var(--border-color)]">
                          <select className="w-full bg-transparent text-sm text-[var(--text-primary)] focus:outline-none cursor-pointer" value={task.party || ''} onChange={(e) => updateGanttTask(task.id, { party: e.target.value as GanttParty | '' })}>
                            <option value="">-</option>
                            <option value="frankieone">FrankieOne</option>
                            <option value="client">Client</option>
                          </select>
                        </td>
                        <td className="px-2 py-1.5 border-b border-l border-[var(--border-color)]">
                          <input type="date" className="w-full bg-transparent text-sm text-[var(--text-primary)] focus:outline-none focus:bg-[var(--bg-tertiary)] rounded px-1" value={task.startDate} onChange={(e) => updateGanttTask(task.id, { startDate: e.target.value })} />
                        </td>
                        <td className="px-2 py-1.5 border-b border-l border-[var(--border-color)]">
                          <input type="date" className="w-full bg-transparent text-sm text-[var(--text-primary)] focus:outline-none focus:bg-[var(--bg-tertiary)] rounded px-1" value={task.endDate} onChange={(e) => updateGanttTask(task.id, { endDate: e.target.value })} />
                        </td>
                        <td className="px-1 py-1.5 border-b border-l border-[var(--border-color)] text-center">
                          <button type="button" className="p-1 text-red-400 hover:bg-red-400/10 rounded" onClick={() => removeGanttTask(task.id)}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Gantt timeline visualization */}
              {(() => {
                const tasks = config.ganttTasks.filter((t) => t.startDate && t.endDate)
                if (tasks.length === 0) return null

                const dates = tasks.flatMap((t) => [new Date(t.startDate).getTime(), new Date(t.endDate).getTime()])
                const minDate = Math.min(...dates)
                const maxDate = Math.max(...dates)
                const range = maxDate - minDate || 1

                const startMonth = new Date(minDate)
                startMonth.setDate(1)
                const monthLabels: { label: string; pct: number }[] = []
                const cursor = new Date(startMonth)
                while (cursor.getTime() <= maxDate) {
                  const pct = ((cursor.getTime() - minDate) / range) * 100
                  if (pct >= 0 && pct <= 100) {
                    monthLabels.push({ label: cursor.toLocaleString('default', { month: 'short', year: '2-digit' }), pct })
                  }
                  cursor.setMonth(cursor.getMonth() + 1)
                }

                return (
                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-[var(--text-primary)] mb-3">Timeline</h5>
                    <div className="relative h-6 mb-1">
                      {monthLabels.map((ml, i) => (
                        <span key={i} className="absolute text-[10px] text-[var(--text-muted)]" style={{ left: `calc(160px + ${ml.pct}% * (100% - 160px) / 100)` }}>
                          {ml.label}
                        </span>
                      ))}
                    </div>
                    <div className="space-y-1.5">
                      {tasks.map((task) => {
                        const start = new Date(task.startDate).getTime()
                        const end = new Date(task.endDate).getTime()
                        const leftPct = ((start - minDate) / range) * 100
                        const widthPct = Math.max(((end - start) / range) * 100, 2)
                        const barColor = task.party === 'client' ? 'bg-amber-400' : 'bg-blue-500'

                        return (
                          <div key={task.id} className="flex items-center gap-2 text-xs">
                            <span className="w-40 flex-shrink-0 truncate text-[var(--text-muted)]">{task.taskName}</span>
                            <div className="flex-1 h-6 bg-[var(--bg-tertiary)] rounded relative">
                              <div
                                className={`absolute h-full rounded ${barColor} opacity-80`}
                                style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                              />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-[var(--text-muted)]">
                      <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-blue-500 opacity-80" /> FrankieOne</span>
                      <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-amber-400 opacity-80" /> Client</span>
                    </div>
                  </div>
                )
              })()}
            </>
          )}
        </Section>

        {/* 6. Key Assumptions */}
        <Section id="keyAssumptions" icon={ClipboardList} iconColor="text-emerald-500" title="Key Assumptions" expandedSections={expandedSections} onToggle={toggleSection}>
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Key Assumptions</label>
            <p className="text-xs text-[var(--text-muted)] mb-2">
              List any assumptions underpinning this SOW (e.g., client resourcing, data availability, timelines). One per line.
            </p>
            <textarea
              className="w-full min-h-32 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary resize-y"
              placeholder="e.g., Client will provide sandbox access within 5 business days of signing..."
              value={config.keyAssumptions || ''}
              onChange={(e) => updateField('keyAssumptions', e.target.value)}
            />
          </div>
        </Section>

        {/* 7. Additional Notes */}
        <div className="bg-[var(--bg-tertiary)]/40 border border-[var(--border-color)] rounded-lg p-4">
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Additional Notes</label>
          <textarea
            className="w-full min-h-32 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary resize-y"
            placeholder="e.g., Special pricing arrangements, dependencies on client actions, assumptions..."
            value={config.notes}
            onChange={(e) => updateField('notes', e.target.value)}
          />
        </div>
      </div>

      {/* Floating Save Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          type="button"
          className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl shadow-lg hover:bg-primary/90 transition-all hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save size={18} />
              Save {activeTypeOption?.label || ''} Template
            </>
          )}
        </button>
      </div>
    </div>
  )
}
